import { StoreType, TInfoFormValues } from '@/types/infoStep'
import { regexPatterns } from '@/utils/constants/regex'
import { z } from 'zod'

export const infoBaseSchema = z.object({
  firstName: z
    .string()
    .trim()
    .regex(
      regexPatterns.persianText,
      'نام فقط باید شامل حروف فارسی و فاصله باشد'
    )
    .nonempty('لطفا نام خود را وارد کنید'),
  lastName: z
    .string()
    .trim()
    .regex(
      regexPatterns.persianText,
      'نام خانوادگی نمی‌تواند شامل عدد یا کاراکترهای ویژه باشد'
    )
    .nonempty('لطفاً نام خانوادگی را وارد کنید'),
  nationalId: z
    .string()
    .min(10, 'کد ملی باید ۱۰ رقم باشد')
    .max(10, 'کد ملی باید ۱۰ رقم باشد')
    .regex(regexPatterns.digitsOnly, 'کد ملی فقط شامل عدد است')
    .nonempty('کد ملی خود را وارد کنید'),
  email: z
    .string()
    .regex(regexPatterns.email, 'فرمت ایمیل معتبر نیست')
    .nonempty('ایمیل خود را وارد کنید'),
  username: z
    .string()
    .trim()
    .min(5, 'نام کاربری حداقل ۵ کاراکتر')
    .max(20, 'نام کاربری حداکثر ۲۰ کاراکتر')
    .regex(regexPatterns.alphanumeric, 'فقط حروف انگلیسی و عدد مجاز است')
    .nonempty('نام کاربری خود را وارد کنید'),
  shebaNumber: z
    .string()
    .min(24, 'شماره شبا باید ۲۴ رقم باشد')
    .max(24, 'شماره شبا باید ۲۴ رقم باشد')
    .regex(regexPatterns.digitsOnly, 'شماره شبا فقط شامل عدد است')
    .nonempty('شماره شبا خود را وارد کنید'),
  userType: z.enum(['individual', 'legal'], {
    required_error: 'نوع حساب را مشخص کنید'
  })
})

export const individualSchema = z.object({
  businessLicenseNumber: z
    .string()
    .optional()
    .refine((val) => !val || regexPatterns.digitsOnly.test(val), {
      message: 'شماره جواز کسب فقط می‌تواند شامل اعداد باشد'
    }),
  isicCode: z
    .string()
    .optional()
    .refine((val) => !val || regexPatterns.digitsOnly.test(val), {
      message: 'شماره آیسیک فقط می‌تواند شامل اعداد باشد'
    }),
  licenseExpirationDate: z
    .string()
    .nullable()
    .optional(),
  licenseCategory: z
    .string()
    .trim()
    .optional()
    .refine((val) => !val || regexPatterns.noEnglishAlphabet.test(val), {
      message: 'رسته جواز کسب فقط می‌تواند شامل حروف فارسی باشد'
    }),
  storeName: z
    .string()
    .regex(
      regexPatterns.brandName,
      'نام فروشگاه فقط می‌تواند شامل حروف فارسی و اعداد باشد'
    )
    .nonempty('لطفا نام فروشگاه را وارد کنید')
})

export const legalSchema = z.object({
  companyNationalId: z
    .string()
    .min(11, 'کد ملی باید حداقل ۱۱ رقم باشد')
    .max(11, 'شناسه ملی باید حداکثر ۱۱ رقم باشد')
    .regex(regexPatterns.digitsOnly, 'شناسه ملی فقط می‌تواند شامل اعداد باشد')
    .nonempty('شناسه ملی شرکت خود را وارد کنید'),
  companyEconomicCode: z
    .string()
    .optional()
    .refine((val) => !val || regexPatterns.digitsOnly.test(val), {
      message: ' کد اقتصادی فقط می‌تواند شامل اعداد باشد'
    }),
  registrationNumber: z
    .string()
    .max(6, 'شماره ثبت باید حداکثر ۶ رقم باشد')
    .regex(regexPatterns.digitsOnly, 'شماره ثبت شرکت فقط باید شامل اعداد باشد')
    .nonempty('لطفاً شماره ثبت شرکت را وارد کنید'),
  companyName: z
    .string()
    .regex(
      regexPatterns.noSpecialChars,
      'نام شرکت نمی‌تواند شامل کاراکترهای نامعتبر باشد'
    )
    .nonempty('لطفا نام شرکت را وارد کنید'),
  storeBrandName: z
    .string()
    .regex(
      regexPatterns.brandName,
      'نام برند فقط می‌تواند شامل حروف فارسی و اعداد باشد'
    )
    .nonempty('نام برند فروشگاه خود را وارد کنید'),
  signatoryOwners: z.string().nonempty('لطفا نام صاحبان امضا را وارد کنید')
})

const isWebsiteRequiredForStoreType = (type: TInfoFormValues['type']) =>
  type === StoreType.ONLINE || type === StoreType.BOTH

export const createInfoDynamicSchema = (values: TInfoFormValues) => {
  let schema = infoBaseSchema
  if (values.userType === 'individual') schema = schema.merge(individualSchema)
  if (values.userType === 'legal') schema = schema.merge(legalSchema)

  if (values.userType === 'individual' || values.userType === 'legal') {
    schema = schema.merge(
      z.object({
        website: isWebsiteRequiredForStoreType(values.type)
          ? z
              .string()
              .trim()
              .refine(
                (val) => val.replace(/^https?:\/\//i, '').trim().length > 0,
                { message: 'لطفا آدرس وبسایت فروشگاه را وارد کنید' }
              )
          : z.string().optional()
      })
    )
  }

  return schema
}
